import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShieldCheckIcon, GlobeIcon, PenToolIcon, ChevronDownIcon, PackageIcon } from 'lucide-react';
import posthog from 'posthog-js';
import { Photo } from '../data/photos';
import { ProtectedImage } from './ProtectedImage';
function FloatingCTA({
  price,
  onScrollDown,
  purchaseRef
}: {
  price: number;
  onScrollDown: () => void;
  purchaseRef: React.RefObject<HTMLDivElement>;
}) {
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const handleScroll = () => {
      if (purchaseRef.current) {
        const rect = purchaseRef.current.getBoundingClientRect();
        setVisible(rect.top > window.innerHeight * 0.8);
      }
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [purchaseRef]);
  return <AnimatePresence data-id="element-120">
      {visible && <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: 20
    }} transition={{
      duration: 0.4
    }} className="fixed bottom-8 left-1/2 -translate-x-1/2 z-40 cursor-pointer" onClick={onScrollDown} data-id="element-121">
          <div className="bg-white/90 backdrop-blur-md border border-gray-200 rounded-full px-6 py-3 shadow-lg flex items-center gap-3 hover:shadow-xl hover:bg-white transition-all" data-id="element-122">
            <span className="text-[11px] font-sans tracking-[0.2em] uppercase text-charcoal" data-id="element-123">
              Limited editions from ${price.toLocaleString()}
            </span>
            <motion.div animate={{
          y: [0, 4, 0]
        }} transition={{
          duration: 1.5,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-124">
              <ChevronDownIcon className="text-charcoal" size={16} strokeWidth={2} data-id="element-125" />
            </motion.div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}
interface PhotoDetailProps {
  photo: Photo | null;
  onClose?: () => void;
  isModal?: boolean;
}
export function PhotoDetail({
  photo,
  onClose,
  isModal = true
}: PhotoDetailProps) {
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [isFramed, setIsFramed] = useState(false);
  const imageRef = useRef<HTMLDivElement>(null);
  const purchaseRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (photo && photo.sizes.length > 0) {
      setSelectedSize(photo.sizes[0].id);
      setIsFramed(false);
      posthog.capture('photo_detail_viewed', {
        photo_id: photo.id,
        photo_title: photo.title,
        photo_category: photo.category
      });
    }
  }, [photo]);
  if (!photo) return null;
  const currentSize = photo.sizes.find(s => s.id === selectedSize) || photo.sizes[0];
  const framePrice = isFramed ? currentSize.frameUpcharge : 0;
  const totalPrice = currentSize.price + framePrice;
  const isLowStock = currentSize.remaining > 0 && currentSize.remaining <= 5;
  const isSoldOut = currentSize.remaining === 0;
  const printNumber = currentSize.editionTotal - currentSize.remaining + 1;
  const handleFrameToggle = (framed: boolean) => {
    setIsFramed(framed);
    if (photo) {
      const size = photo.sizes.find(s => s.id === selectedSize) || photo.sizes[0];
      const fPrice = framed ? size.frameUpcharge : 0;
      posthog.capture('frame_option_toggled', {
        photo_id: photo.id,
        is_framed: framed,
        total_price: size.price + fPrice
      });
    }
    if (imageRef.current) {
      imageRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  };
  const scrollToPurchase = () => {
    if (photo) {
      posthog.capture('floating_cta_clicked', {
        photo_id: photo.id,
        photo_title: photo.title
      });
    }
    if (purchaseRef.current) {
      purchaseRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  if (!isModal) {
    return <div className="min-h-screen bg-white" data-id="element-126">
        {/* Section 1: Hero - Image + Story */}
        <div className="w-full bg-white" data-id="element-127">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-20" data-id="element-128">
            <div className="grid md:grid-cols-[55%_45%] gap-8 md:gap-12 items-start" data-id="element-129">
              {/* Left: Image */}
              <motion.div ref={imageRef} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.2,
              duration: 0.6
            }} className="bg-gray-50 p-6 md:p-10 flex items-center justify-center" data-id="element-130">
                <div className={`transition-all duration-500 ${isFramed ? 'p-4 bg-white border-8 border-charcoal shadow-2xl' : ''}`} data-id="element-131">
                  <ProtectedImage src={photo.imageUrl} alt={photo.title} className="w-full max-h-[70vh] object-contain" wrapperClassName="flex items-center justify-center" data-id="element-132" />
                </div>
              </motion.div>

              {/* Right: Story */}
              <motion.div initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.3,
              duration: 0.6
            }} className="flex flex-col justify-center" data-id="element-133">
                {/* Title */}
                <div className="mb-10" data-id="element-134">
                  <p className="text-[10px] font-sans tracking-[0.25em] text-gray-400 uppercase mb-5" data-id="element-135">
                    {photo.collection}
                  </p>
                  <h1 className="text-4xl md:text-5xl font-serif text-charcoal mb-4" data-id="element-136">
                    {photo.title}
                  </h1>
                  <p className="text-xs font-sans tracking-[0.15em] text-gray-400 uppercase" data-id="element-137">
                    {photo.location}, {photo.year}
                  </p>
                </div>

                {/* Narrative */}
                <div className="space-y-5 text-base font-sans leading-relaxed text-gray-600 mb-10" data-id="element-138">
                  {photo.narrativeDescription.split('\n\n').map((paragraph, idx) => <p key={idx} data-id="element-139">{paragraph}</p>)}
                </div>

                {/* Edition Info */}
                <div className="border-t border-gray-100 pt-8 space-y-3" data-id="element-140">
                  <p className="text-sm font-sans text-charcoal tracking-wide" data-id="element-141">
                    Print {printNumber}/{currentSize.editionTotal}
                  </p>
                  <p className={`text-xs font-sans tracking-[0.1em] uppercase ${isLowStock ? 'text-amber-700 font-medium' : isSoldOut ? 'text-red-700 font-medium' : 'text-gray-500'}`} data-id="element-142">
                    {isSoldOut ? 'Sold out in this size' : `Edition of ${currentSize.editionTotal} — ${currentSize.remaining} remaining`}
                  </p>
                  {isLowStock && <motion.p initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} className="text-[10px] font-sans tracking-[0.15em] text-amber-600 uppercase" data-id="element-143">
                      Only {currentSize.remaining} left — collect now
                    </motion.p>}
                  <p className="text-xs font-sans text-gray-400 italic mt-2" data-id="element-144">
                    Includes signed certificate of authenticity
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Floating CTA Indicator - Fixed at bottom of viewport */}
        <FloatingCTA price={photo.sizes[0].price} onScrollDown={scrollToPurchase} purchaseRef={purchaseRef} data-id="element-145" />

        {/* Section 3: Purchase Options */}
        <motion.div ref={purchaseRef} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.6,
        duration: 0.6
      }} className="w-full bg-white py-16 md:py-24 border-t border-gray-100" data-id="element-146">
          <div className="max-w-5xl mx-auto px-6 md:px-12" data-id="element-147">
            <div className="grid md:grid-cols-2 gap-12 md:gap-16" data-id="element-148">
              {/* Left: Size + Frame Selection */}
              <div className="space-y-8" data-id="element-149">
                {/* Size Selection */}
                <div data-id="element-150">
                  <label className="block text-xs font-sans tracking-[0.15em] uppercase text-charcoal mb-5" data-id="element-151">
                    Select Size
                  </label>
                  <div className="space-y-3" data-id="element-152">
                    {photo.sizes.map(size => {
                    const sizeIsLow = size.remaining > 0 && size.remaining <= 5;
                    const sizeSoldOut = size.remaining === 0;
                    return <label key={size.id} className={`flex items-center justify-between p-4 border cursor-pointer transition-all ${sizeSoldOut ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed' : selectedSize === size.id ? 'border-charcoal bg-gray-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-153">
                          <div className="flex items-center space-x-4" data-id="element-154">
                            <input type="radio" name="size" value={size.id} checked={selectedSize === size.id} onChange={e => {
                          setSelectedSize(e.target.value);
                          posthog.capture('print_size_selected', {
                            photo_id: photo.id,
                            size_id: size.id,
                            size_label: size.label,
                            price: size.price
                          });
                        }} disabled={sizeSoldOut} className="w-4 h-4 text-charcoal focus:ring-charcoal border-gray-300" data-id="element-155" />
                            <div data-id="element-156">
                              <span className="block text-sm font-medium text-charcoal" data-id="element-157">
                                {size.label} ({size.dimensions})
                              </span>
                              <span className={`block text-[11px] mt-1 ${sizeIsLow ? 'text-amber-700 font-medium' : sizeSoldOut ? 'text-red-600' : 'text-gray-400'}`} data-id="element-158">
                                {sizeSoldOut ? 'Sold out' : `Edition of ${size.editionTotal} — ${size.remaining} remaining`}
                              </span>
                            </div>
                          </div>
                          <span className="text-sm text-charcoal font-medium" data-id="element-159">
                            ${size.price.toLocaleString()}
                          </span>
                        </label>;
                  })}
                  </div>
                </div>

                {/* Frame Selection */}
                <div data-id="element-160">
                  <label className="block text-xs font-sans tracking-[0.15em] uppercase text-charcoal mb-5" data-id="element-161">
                    Presentation
                  </label>
                  <div className="flex space-x-4" data-id="element-162">
                    <button onClick={() => handleFrameToggle(false)} className={`flex-1 py-3 text-sm border transition-all ${!isFramed ? 'border-charcoal bg-charcoal text-white' : 'border-gray-200 text-charcoal hover:border-gray-300'}`} data-id="element-163">
                      Unframed
                    </button>
                    <button onClick={() => handleFrameToggle(true)} className={`flex-1 py-3 text-sm border transition-all ${isFramed ? 'border-charcoal bg-charcoal text-white' : 'border-gray-200 text-charcoal hover:border-gray-300'}`} data-id="element-164">
                      Framed (+${currentSize.frameUpcharge})
                    </button>
                  </div>
                </div>
              </div>

              {/* Right: Price + CTA + Trust Signals */}
              <div className="space-y-8" data-id="element-165">
                {/* Price */}
                <div className="border-t border-b border-gray-100 py-8" data-id="element-166">
                  <div className="flex justify-between items-end mb-2" data-id="element-167">
                    <span className="text-xs font-sans tracking-[0.15em] uppercase text-gray-400" data-id="element-168">
                      Total Price
                    </span>
                    <span className="text-4xl font-serif text-charcoal" data-id="element-169">
                      ${totalPrice.toLocaleString()}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 text-right" data-id="element-170">
                    {isFramed ? 'Framed' : 'Unframed'} · {currentSize.label}
                  </p>
                </div>

                {/* CTAs */}
                <div className="space-y-3" data-id="element-171">
                  <a href={isSoldOut ? undefined : `https://wa.me/5491164148961?text=${encodeURIComponent(`Hi! I'd like to purchase the print "${photo.title}".\n\nSize: ${currentSize.label} (${currentSize.dimensions})\nPresentation: ${isFramed ? 'Framed' : 'Unframed'}\nTotal: ${totalPrice.toLocaleString()}\n\nPlease let me know the next steps. Thank you!`)}`} target="_blank" rel="noopener noreferrer" className={`block w-full text-center py-4 text-xs font-medium tracking-[0.2em] uppercase transition-all ${isSoldOut ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-charcoal text-white hover:bg-black'}`} onClick={isSoldOut ? (e: React.MouseEvent) => e.preventDefault() : () => posthog.capture('purchase_whatsapp_clicked', {
                  photo_id: photo.id,
                  photo_title: photo.title,
                  size_id: currentSize.id,
                  is_framed: isFramed,
                  total_price: totalPrice
                })} data-id="element-172">
                    {isSoldOut ? 'Sold Out' : 'Purchase Print'}
                  </a>
                  <a href={`mailto:inquiries@alenoriega.com?subject=Inquiry: ${photo.title}&body=I am interested in "${photo.title}" — ${currentSize.label} (${currentSize.dimensions}), ${isFramed ? 'Framed' : 'Unframed'}.`} onClick={() => posthog.capture('inquiry_email_clicked', {
                  photo_id: photo.id,
                  photo_title: photo.title,
                  size_id: currentSize.id
                })} className="block w-full bg-transparent border border-gray-300 text-charcoal text-center py-3.5 text-[10px] font-medium tracking-[0.2em] uppercase transition-all hover:border-charcoal hover:bg-gray-50" data-id="element-173">
                    Inquire
                  </a>
                </div>

                {/* Trust Signals */}
                <div className="pt-6 border-t border-gray-50 space-y-4" data-id="element-174">
                  <div className="flex items-center space-x-3 text-gray-400" data-id="element-175">
                    <ShieldCheckIcon size={16} strokeWidth={1.5} data-id="element-176" />
                    <span className="text-xs font-sans tracking-[0.1em]" data-id="element-177">
                      Printed on{' '}
                      <a href="https://www.hahnemuehle.com/en/digital-papers/fineart-collection/natural-line/p/Product/show/202/21.html" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-charcoal transition-colors" data-id="element-178">
                        Hahnemühle Photo Rag
                      </a>{' '}
                      — museum-quality archival paper
                    </span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-400" data-id="element-179">
                    <PenToolIcon size={16} strokeWidth={1.5} data-id="element-180" />
                    <span className="text-xs font-sans tracking-[0.1em] uppercase" data-id="element-181">
                      Signed by the artist
                    </span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-400" data-id="element-182">
                    <GlobeIcon size={16} strokeWidth={1.5} data-id="element-183" />
                    <span className="text-xs font-sans tracking-[0.1em] uppercase" data-id="element-184">
                      Ships worldwide
                    </span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-400" data-id="element-185">
                    <PackageIcon size={16} strokeWidth={1.5} data-id="element-186" />
                    <span className="text-xs font-sans tracking-[0.1em] uppercase" data-id="element-187">
                      Shipping at buyer's expense
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>;
  }
  // Modal version - COMPLETELY UNTOUCHED below this line
  return <AnimatePresence data-id="element-188">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex items-center justify-center bg-white" data-id="element-189">
        {onClose && <button onClick={onClose} className="absolute top-6 right-6 md:top-10 md:right-10 z-50 p-2 text-charcoal hover:opacity-60 transition-opacity" data-id="element-190">
            <X size={32} strokeWidth={1.5} data-id="element-191" />
          </button>}
        <div className="w-full h-full flex flex-col md:flex-row overflow-y-auto md:overflow-hidden" data-id="element-192">
          {/* Image Section */}
          <div className="w-full md:w-3/5 h-[50vh] md:h-full bg-offwhite flex items-center justify-center p-8 md:p-16" data-id="element-193">
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.2,
            duration: 0.5
          }} className={`max-w-full max-h-full transition-all duration-500 ${isFramed ? 'p-4 bg-white border-8 border-charcoal shadow-2xl' : 'shadow-2xl'}`} data-id="element-194">
              <ProtectedImage src={photo.imageUrl} alt={photo.title} className="max-w-full max-h-[60vh] md:max-h-[75vh] object-contain" wrapperClassName="flex items-center justify-center" data-id="element-195" />
            </motion.div>
          </div>

          {/* Details Section */}
          <div className="w-full md:w-2/5 h-auto md:h-full overflow-y-auto p-8 md:p-16 lg:p-24 flex flex-col justify-center bg-white" data-id="element-196">
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.3,
            duration: 0.5
          }} className="max-w-md mx-auto w-full" data-id="element-197">
              {/* Title */}
              <div className="mb-10" data-id="element-198">
                <p className="text-[10px] font-sans tracking-[0.25em] text-gray-400 uppercase mb-5" data-id="element-199">
                  {photo.collection}
                </p>
                <h2 className="text-4xl md:text-5xl font-serif text-charcoal mb-3" data-id="element-200">
                  {photo.title}
                </h2>
                <p className="text-xs font-sans tracking-[0.15em] text-gray-400 uppercase" data-id="element-201">
                  {photo.location}, {photo.year}
                </p>
              </div>

              {/* Narrative description */}
              <div className="space-y-4 text-sm font-sans leading-relaxed text-gray-500 mb-14" data-id="element-202">
                {photo.narrativeDescription.split('\n\n').map((paragraph, idx) => <p key={idx} data-id="element-203">{paragraph}</p>)}
              </div>

              {/* Edition + Scarcity */}
              <div className="border-t border-b border-gray-100 py-8 mb-10 space-y-3" data-id="element-204">
                <p className="text-sm font-sans text-charcoal tracking-wide" data-id="element-205">
                  Print {printNumber}/{currentSize.editionTotal}
                </p>
                <p className={`text-xs font-sans tracking-[0.1em] uppercase ${isLowStock ? 'text-amber-700 font-medium' : isSoldOut ? 'text-red-700 font-medium' : 'text-gray-500'}`} data-id="element-206">
                  {isSoldOut ? 'Sold out in this size' : `Edition of ${currentSize.editionTotal} — ${currentSize.remaining} remaining`}
                </p>
                {isLowStock && <motion.p initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} className="text-[10px] font-sans tracking-[0.15em] text-amber-600 uppercase" data-id="element-207">
                    Only {currentSize.remaining} left — collect now
                  </motion.p>}
                <p className="text-xs font-sans text-gray-400 italic mt-2" data-id="element-208">
                  Includes signed certificate of authenticity
                </p>
              </div>

              <div className="space-y-10" data-id="element-209">
                {/* Size Selection */}
                <div data-id="element-210">
                  <label className="block text-xs font-sans tracking-[0.15em] uppercase text-charcoal mb-4" data-id="element-211">
                    Select Size
                  </label>
                  <div className="space-y-3" data-id="element-212">
                    {photo.sizes.map(size => {
                    const sizeIsLow = size.remaining > 0 && size.remaining <= 5;
                    const sizeSoldOut = size.remaining === 0;
                    return <label key={size.id} className={`flex items-center justify-between p-4 border cursor-pointer transition-all ${sizeSoldOut ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed' : selectedSize === size.id ? 'border-charcoal bg-gray-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-213">
                          <div className="flex items-center space-x-4" data-id="element-214">
                            <input type="radio" name="size" value={size.id} checked={selectedSize === size.id} onChange={e => {
                          setSelectedSize(e.target.value);
                          posthog.capture('print_size_selected', {
                            photo_id: photo.id,
                            size_id: size.id,
                            size_label: size.label,
                            price: size.price
                          });
                        }} disabled={sizeSoldOut} className="w-4 h-4 text-charcoal focus:ring-charcoal border-gray-300" data-id="element-215" />
                            <div data-id="element-216">
                              <span className="block text-sm font-medium text-charcoal" data-id="element-217">
                                {size.label} ({size.dimensions})
                              </span>
                              <span className={`block text-[11px] mt-1 ${sizeIsLow ? 'text-amber-700 font-medium' : sizeSoldOut ? 'text-red-600' : 'text-gray-400'}`} data-id="element-218">
                                {sizeSoldOut ? 'Sold out' : `Edition of ${size.editionTotal} — ${size.remaining} remaining`}
                              </span>
                            </div>
                          </div>
                          <span className="text-sm text-charcoal font-medium" data-id="element-219">
                            ${size.price.toLocaleString()}
                          </span>
                        </label>;
                  })}
                  </div>
                </div>

                {/* Frame Selection */}
                <div data-id="element-220">
                  <label className="block text-xs font-sans tracking-[0.15em] uppercase text-charcoal mb-4" data-id="element-221">
                    Presentation
                  </label>
                  <div className="flex space-x-4" data-id="element-222">
                    <button onClick={() => {
                    setIsFramed(false);
                    const fPrice = 0;
                    posthog.capture('frame_option_toggled', {
                      photo_id: photo.id,
                      is_framed: false,
                      total_price: currentSize.price + fPrice
                    });
                  }} className={`flex-1 py-3 text-sm border transition-all ${!isFramed ? 'border-charcoal bg-charcoal text-white' : 'border-gray-200 text-charcoal hover:border-gray-300'}`} data-id="element-223">
                      Unframed
                    </button>
                    <button onClick={() => {
                    setIsFramed(true);
                    posthog.capture('frame_option_toggled', {
                      photo_id: photo.id,
                      is_framed: true,
                      total_price: currentSize.price + currentSize.frameUpcharge
                    });
                  }} className={`flex-1 py-3 text-sm border transition-all ${isFramed ? 'border-charcoal bg-charcoal text-white' : 'border-gray-200 text-charcoal hover:border-gray-300'}`} data-id="element-224">
                      Framed (+${currentSize.frameUpcharge})
                    </button>
                  </div>
                </div>

                {/* Price + CTA */}
                <div className="pt-10 border-t border-gray-100" data-id="element-225">
                  <div className="flex justify-between items-end mb-10" data-id="element-226">
                    <span className="text-xs font-sans tracking-[0.15em] uppercase text-gray-400" data-id="element-227">
                      Total
                    </span>
                    <span className="text-3xl font-serif text-charcoal" data-id="element-228">
                      ${totalPrice.toLocaleString()}
                    </span>
                  </div>

                  <div className="space-y-3" data-id="element-229">
                    <a href={isSoldOut ? undefined : `https://wa.me/5491164148961?text=${encodeURIComponent(`Hi! I'd like to purchase the print "${photo.title}".\n\nSize: ${currentSize.label} (${currentSize.dimensions})\nPresentation: ${isFramed ? 'Framed' : 'Unframed'}\nTotal: ${totalPrice.toLocaleString()}\n\nPlease let me know the next steps. Thank you!`)}`} target="_blank" rel="noopener noreferrer" className={`block w-full text-center py-4 text-xs font-medium tracking-[0.2em] uppercase transition-all ${isSoldOut ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-charcoal text-white hover:bg-black'}`} onClick={isSoldOut ? (e: React.MouseEvent) => e.preventDefault() : () => posthog.capture('purchase_whatsapp_clicked', {
                    photo_id: photo.id,
                    photo_title: photo.title,
                    size_id: currentSize.id,
                    is_framed: isFramed,
                    total_price: totalPrice
                  })} data-id="element-230">
                      {isSoldOut ? 'Sold Out' : 'Buy Print'}
                    </a>
                    <a href={`mailto:inquiries@alenoriega.com?subject=Inquiry: ${photo.title}&body=I am interested in "${photo.title}" — ${currentSize.label} (${currentSize.dimensions}), ${isFramed ? 'Framed' : 'Unframed'}.`} onClick={() => posthog.capture('inquiry_email_clicked', {
                    photo_id: photo.id,
                    photo_title: photo.title,
                    size_id: currentSize.id
                  })} className="block w-full bg-transparent border border-gray-300 text-charcoal text-center py-3.5 text-[10px] font-medium tracking-[0.2em] uppercase transition-all hover:border-charcoal hover:bg-gray-50" data-id="element-231">
                      Inquire
                    </a>
                  </div>

                  {/* Trust & Value Signals */}
                  <div className="mt-10 pt-8 border-t border-gray-50 space-y-4" data-id="element-232">
                    <div className="flex items-center space-x-3 text-gray-400" data-id="element-233">
                      <ShieldCheckIcon size={14} strokeWidth={1.5} data-id="element-234" />
                      <span className="text-[10px] font-sans tracking-[0.15em] uppercase" data-id="element-235">
                        Archival museum-quality print
                      </span>
                    </div>
                    <div className="flex items-center space-x-3 text-gray-400" data-id="element-236">
                      <GlobeIcon size={14} strokeWidth={1.5} data-id="element-237" />
                      <span className="text-[10px] font-sans tracking-[0.15em] uppercase" data-id="element-238">
                        Ships worldwide
                      </span>
                    </div>
                    <div className="flex items-center space-x-3 text-gray-400" data-id="element-239">
                      <PenToolIcon size={14} strokeWidth={1.5} data-id="element-240" />
                      <span className="text-[10px] font-sans tracking-[0.15em] uppercase" data-id="element-241">
                        Signed by the artist
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>;
}